import { EChartOption } from 'echarts';
import moment from 'moment';
import { MetricDto } from '@/core';
import { getDefaultOptions } from './getDefaultOptions';
import { GroupedMetricType, getGroupedMetrics } from './getGroupedMetrics';
import { displayCurrency } from '@/utils/displayCurrency';

export enum TotalsTypesEnum {
  costs = 'Sum $',
  percentage = 'Percentage',
}

type LastMetricsForEachMonthType = {
  [key: string]: string;
};

const getLastMetricForEachMonth = (legendData: string[], metricsGroupedByMonth: GroupedMetricType[]) => {
  return metricsGroupedByMonth.reduce((acc, [month, metrics]) => {
    const metricsSortedSameAsLegends = metrics.sort(
      (a, b) => legendData.indexOf(a.name) - legendData.indexOf(b.name),
    );

    return Object.assign(acc, {
      [moment(month).format()]: metricsSortedSameAsLegends?.at(-1)?.name,
    });
  }, {} as LastMetricsForEachMonthType);
};

const calculateTotalCostsOnMonth = (metricsGroupedByMonth: GroupedMetricType[]) =>
  metricsGroupedByMonth.map(([month, array]) => array.reduce((acc, { totalCost }) => acc + totalCost, 0));

const tooltipFormatter = (param: EChartOption.Tooltip.Format[], totalType: TotalsTypesEnum) => {
  const formattedValue = (data: number) =>
    totalType === TotalsTypesEnum.percentage ? `${data.toFixed(2)}%` : `$${displayCurrency(data)}`;

  const valuesData = param
    .map(
      ({ marker, seriesName, data }) => `
        <div class="echarts-tooltip-content-cost">
          <div>${marker}
            <span>${seriesName}</span>
          </div>
          <div class="echarts-tooltip-value">${formattedValue(data.value.at(1))}</div>
        </div>`,
    )
    .join('');

  return `<div>
          ${moment(param[0].axisValue).format('MMMM, YYYY')}
        </div>
        <div class="echarts-tooltip-content">
         ${valuesData}
        </div>`;
};

const emphasisStyle = {
  itemStyle: {
    borderWidth: 4,
    borderColor: '#1677FF66',
  },
};

const getLegend = (data: string[]) => ({
  type: 'scroll',
  selectedMode: true,
  padding: [0, 16],
  bottom: 16,
  itemWidth: 8,
  itemHeight: 8,
  textStyle: {
    fontSize: 12,
  },
  itemGap: 24,
  icon: 'circle',
  data,
});

const getSeriesData = (
  metrics: MetricDto[],
  lastMetricsForEachMonth: LastMetricsForEachMonthType,
  metricName: string,
) => {
  return metrics.map(({ totalCost, month }) => {
    const isMetricLastInStack = lastMetricsForEachMonth[moment(month).format()] === metricName;

    if (isMetricLastInStack) {
      return {
        value: [month, totalCost],
        itemStyle: {
          borderRadius: [7, 7, 0, 0],
        },
      };
    }
    return { value: [month, totalCost] };
  });
};

const getPercentageSeriesData = (
  metrics: MetricDto[],
  lastMetricsForEachMonth: LastMetricsForEachMonthType,
  metricName: string,
  totalCostsOnEachMonth: number[],
) => {
  return metrics.map(({ totalCost, month }, index) => {
    const percentageValue = [month, (totalCost * 100) / totalCostsOnEachMonth[index]];
    const isMetricLastInStack = lastMetricsForEachMonth[moment(month).format()] === metricName;

    if (isMetricLastInStack) {
      return {
        value: percentageValue,
        itemStyle: {
          borderRadius: [7, 7, 0, 0],
        },
      };
    }
    return { value: percentageValue };
  });
};

const getTotalOptions = (
  isChartExpanded: boolean,
  metricsGroupedByName: GroupedMetricType[],
  metricsGroupedByMonth: GroupedMetricType[],
  maxShownColumns: number,
) => {
  const defaultOptions = getDefaultOptions(isChartExpanded, maxShownColumns);
  const legendData = metricsGroupedByName.map(([name]) => name);
  const lastMetricsForEachMonth = getLastMetricForEachMonth(legendData, metricsGroupedByMonth);

  const series = metricsGroupedByName.map(([name, metrics]) => {
    return {
      name,
      data: getSeriesData(metrics, lastMetricsForEachMonth, name),
      type: 'bar',
      stack: 'total',
      barWidth: 14,
      emphasis: emphasisStyle,
    };
  });

  return {
    ...defaultOptions,
    tooltip: {
      className: 'echarts-tooltip',
      trigger: 'axis',
      id: 'tooltip',
      formatter: (param: EChartOption.Tooltip.Format[]) => tooltipFormatter(param, TotalsTypesEnum.costs),
    },
    yAxis: {
      type: 'value',
      id: 'yAxis',
      axisLabel: {
        formatter: (value: number) => (value ? `$${value / 1000}K` : value),
      },
    },
    series,
    legend: getLegend(legendData),
  } as EChartOption;
};

const getPercentageOptions = (
  isChartExpanded: boolean,
  metricsGroupedByName: GroupedMetricType[],
  metricsGroupedByMonth: GroupedMetricType[],
  maxShownColumns: number,
) => {
  const defaultOptions = getDefaultOptions(isChartExpanded, maxShownColumns);
  const legendData = metricsGroupedByName.map(([name]) => name);
  const lastMetricsForEachMonth = getLastMetricForEachMonth(legendData, metricsGroupedByMonth);
  const totalCostsOnEachMonth = calculateTotalCostsOnMonth(metricsGroupedByMonth);

  const series = metricsGroupedByName.map(([name, metrics]) => ({
    name,
    data: getPercentageSeriesData(metrics, lastMetricsForEachMonth, name, totalCostsOnEachMonth),
    type: 'bar',
    stack: 'total',
    barWidth: 14,
    emphasis: emphasisStyle,
  }));

  return {
    ...defaultOptions,
    yAxis: {
      id: 'yAxis',
      axisLabel: {
        formatter: (value: number) => (value ? `${value}%` : value),
      },
    },
    series,
    tooltip: {
      className: 'echarts-tooltip',
      trigger: 'axis',
      id: 'tooltip',
      formatter: (param: EChartOption.Tooltip.Format[]) =>
        tooltipFormatter(param, TotalsTypesEnum.percentage),
    },
    legend: getLegend(legendData),
  } as EChartOption;
};

export const getOptionsDistributionChart = (
  totalType: string,
  maxShownColumns: number,
  metricsData?: MetricDto[],
) => {
  if (!metricsData) return;

  const { metricsGroupedByMonth, metricsGroupedByName } = getGroupedMetrics(metricsData);

  const isChartExpanded = metricsGroupedByMonth?.length > maxShownColumns;

  const totalOptions = getTotalOptions(
    isChartExpanded,
    metricsGroupedByName,
    metricsGroupedByMonth,
    maxShownColumns,
  );
  const percentageOptions = getPercentageOptions(
    isChartExpanded,
    metricsGroupedByName,
    metricsGroupedByMonth,
    maxShownColumns,
  );

  const options = totalType === TotalsTypesEnum.costs ? totalOptions : percentageOptions;

  return {
    options,
    isChartExpanded,
  };
};
